import type { AxiosInstance } from 'axios';
import type {
  DocumentReview,
  CreateReviewRequest,
  ReviewActivity,
  ReviewAnnotation,
  ReviewAnnotationComment,
} from '../types/review';
import type { EnvelopeField } from '../types/envelope';

export function createReviewService(client: AxiosInstance) {
  return {
    create: (data: CreateReviewRequest) =>
      client.post<DocumentReview>('/v1/reviews', data).then(r => r.data),

    getForTarget: (targetType: string, targetId: string) =>
      client.get<DocumentReview[]>('/v1/reviews', { params: { targetType, targetId } }).then(r => r.data),

    getById: (reviewId: string) =>
      client.get<DocumentReview>(`/v1/reviews/${reviewId}`).then(r => r.data),

    complete: (reviewId: string) =>
      client.post<DocumentReview>(`/v1/reviews/${reviewId}/complete`).then(r => r.data),

    cancel: (reviewId: string) =>
      client.post<DocumentReview>(`/v1/reviews/${reviewId}/cancel`).then(r => r.data),

    respond: (reviewId: string, message: string) =>
      client.post<DocumentReview>(`/v1/reviews/${reviewId}/respond`, { message }).then(r => r.data),

    markAuthorComplete: (reviewId: string) =>
      client.post<DocumentReview>(`/v1/reviews/${reviewId}/author-complete`).then(r => r.data),

    getActivities: (reviewId: string) =>
      client.get<ReviewActivity[]>(`/v1/reviews/${reviewId}/activities`).then(r => r.data),

    addReviewer: (reviewId: string, name: string, email: string) =>
      client.post<DocumentReview>(`/v1/reviews/${reviewId}/reviewers`, { name, email }).then(r => r.data),

    removeReviewer: (reviewId: string, reviewerId: string) =>
      client.delete(`/v1/reviews/${reviewId}/reviewers/${reviewerId}`),

    addNote: (reviewId: string, message: string) =>
      client.post<ReviewActivity>(`/v1/reviews/${reviewId}/note`, { message }).then(r => r.data),

    // ── Session-authed reviewer-* endpoints ─────────────────────────────
    // For INTERNAL reviewers (tenant users + workpay colleagues). Authorization
    // is X-User-Id match against document_reviewer.user_id — no permission code.
    // Distinct from the author-scoped endpoints above (getActivities, addNote,
    // etc.) which require CONTRACT_TEMPLATES_* permissions.

    /** GET /v1/reviews/{reviewId}/reviewer-activities — activity log filtered to caller's reviewer context. */
    getReviewerActivities: (reviewId: string) =>
      client.get<ReviewActivity[]>(`/v1/reviews/${reviewId}/reviewer-activities`).then(r => r.data),

    /** POST /v1/reviews/{reviewId}/reviewer-note — reviewer posts a chat note. */
    addReviewerNote: (reviewId: string, message: string) =>
      client.post<ReviewActivity>(`/v1/reviews/${reviewId}/reviewer-note`, { message }).then(r => r.data),

    /**
     * POST /v1/reviews/{reviewId}/reviewer-submit — submit tracked changes back to the author.
     * Fires CHANGES_SUBMITTED activity, notifies author. Does NOT auto-mark reviewer complete.
     */
    reviewerSubmit: (reviewId: string, message?: string) =>
      client.post<DocumentReview>(
        `/v1/reviews/${reviewId}/reviewer-submit`,
        message ? { message } : {},
      ).then(r => r.data),

    /**
     * POST /v1/reviews/{reviewId}/reviewer-complete — mark the caller's reviewer row REVIEWED.
     * Triggers dual-completion check: if author also done, review transitions COMPLETED.
     */
    reviewerComplete: (reviewId: string) =>
      client.post<DocumentReview>(`/v1/reviews/${reviewId}/reviewer-complete`).then(r => r.data),

    forceSave: (docKey: string) =>
      client.post(`/v1/onlyoffice/forcesave`, null, { params: { key: docKey } }),

    // ── PDF review annotations (author-scoped) ───────────────────────────

    listAnnotations: (reviewId: string) =>
      client.get<ReviewAnnotation[]>(`/v1/reviews/${reviewId}/annotations`).then(r => r.data),

    resolveAnnotation: (reviewId: string, annotationId: string) =>
      client
        .post<ReviewAnnotation>(`/v1/reviews/${reviewId}/annotations/${annotationId}/resolve`)
        .then(r => r.data),

    replyToAnnotation: (reviewId: string, annotationId: string, message: string) =>
      client
        .post<ReviewAnnotation>(
          `/v1/reviews/${reviewId}/annotations/${annotationId}/reply`,
          { message },
        )
        .then(r => r.data),

    deleteAnnotationAsAuthor: (reviewId: string, annotationId: string) =>
      client.delete(`/v1/reviews/${reviewId}/annotations/${annotationId}`),

    // ── Annotation comment threads (author-scoped) ───────────────────────

    listAnnotationComments: (reviewId: string, annotationId: string) =>
      client
        .get<ReviewAnnotationComment[]>(
          `/v1/reviews/${reviewId}/annotations/${annotationId}/comments`,
        )
        .then(r => r.data),

    addAnnotationComment: (reviewId: string, annotationId: string, message: string) =>
      client
        .post<ReviewAnnotationComment>(
          `/v1/reviews/${reviewId}/annotations/${annotationId}/comments`,
          { message },
        )
        .then(r => r.data),

    deleteAnnotationCommentAsAuthor: (reviewId: string, commentId: string) =>
      client.delete(`/v1/reviews/${reviewId}/annotations/comments/${commentId}`),

    // ── Document replace (author uploads revised PDF) ────────────────────

    /**
     * Author uploads a revised document during review.
     * For multi-doc reviews, pass reviewDocumentId; for legacy single-doc reviews, omit it.
     */
    replaceReviewDocument: (reviewId: string, file: File, reviewDocumentId?: string) => {
      const form = new FormData();
      form.append('file', file);
      if (reviewDocumentId) form.append('reviewDocumentId', reviewDocumentId);
      // Unset Content-Type so axios/browser set multipart/form-data with boundary.
      return client
        .post<DocumentReview>(`/v1/reviews/${reviewId}/documents/replace`, form, {
          headers: { 'Content-Type': undefined as unknown as string },
        })
        .then(r => r.data);
    },

    // ── Author prefilled values mid-review ───────────────────────────────

    /** List AUTHOR-prefilled fields for the Edit Values modal. Pass reviewDocumentId for multi-doc reviews. */
    listAuthorFieldsForReview: (reviewId: string, reviewDocumentId?: string) => {
      const path = reviewDocumentId
        ? `/v1/reviews/${reviewId}/documents/${reviewDocumentId}/author-fields`
        : `/v1/reviews/${reviewId}/author-fields`;
      return client.get<EnvelopeField[]>(path).then(r => r.data);
    },

    /** Author updates prefilled values; backend creates a new document version. */
    updateAuthorValues: (
      reviewId: string,
      reviewDocumentId: string | undefined,
      entries: { fieldId: string; value: string }[],
    ) =>
      client
        .post<DocumentReview>(`/v1/reviews/${reviewId}/author-fields`, {
          reviewDocumentId,
          entries,
        })
        .then(r => r.data),
  };
}
